/**
 * Catálogo de templates de notificação.
 *
 * ---
 *
 * POR QUE O CATÁLOGO MORA NO CÓDIGO, E NÃO SÓ NO BANCO
 *
 * A tabela `notification_templates` guarda DUAS coisas diferentes: os textos
 * padrão da plataforma (`tenant_id` nulo, criados pelo seed) e as
 * personalizações de cada barbearia (`tenant_id` preenchido). O padrão precisa
 * existir também aqui porque um banco recém-migrado, sem seed, não pode deixar
 * o sistema mudo: um lembrete que não sai por falta de linha em tabela é uma
 * falha invisível — ninguém reclama de uma mensagem que nunca chegou.
 *
 * Então a resolução tem três degraus, nesta ordem: template da barbearia →
 * template global no banco → este catálogo. O último degrau nunca falha.
 *
 * ---
 *
 * DOIS FORMATOS DE PLACEHOLDER, E POR QUE ISSO NÃO É DESCUIDO
 *
 * Aqui os placeholders têm NOME (`{{customer}}`): é o que uma barbearia
 * consegue editar sem errar. Na Meta, o template aprovado usa POSIÇÃO
 * (`{{1}}`, `{{2}}`) e `components[].parameters[]` é um array — trocar a ordem
 * troca o significado da mensagem, sem erro nenhum.
 *
 * `variables` é a ponte: ela declara a ORDEM em que os nomes viram posições.
 * Renderizar usa os nomes; enviar usa `orderTemplateVariables()`. Nenhum dos
 * dois lados precisa saber do outro.
 */
import { NotificationTemplateKey } from './enums';

export interface NotificationTemplateDefinition {
  readonly key: NotificationTemplateKey;
  /** Nome do template aprovado na Meta. Por convenção, igual à chave. */
  readonly providerTemplateName: string;
  /** Texto com placeholders nomeados: `{{customer}}`, `{{time}}`… */
  readonly bodyTemplate: string;
  /**
   * Nomes das variáveis NA ORDEM em que entram em `components[].parameters[]`
   * do WhatsApp Cloud API. A ordem é parte do contrato com a Meta.
   */
  readonly variables: readonly string[];
}

/** Valores das variáveis de um template, por nome. */
export type TemplateValues = Readonly<Record<string, string>>;

export const DEFAULT_NOTIFICATION_TEMPLATES: readonly NotificationTemplateDefinition[] = [
  {
    key: NotificationTemplateKey.OTP_CODE,
    providerTemplateName: 'otp_code',
    bodyTemplate:
      'Seu código de acesso {{barbershop}} é {{code}}. Ele expira em {{minutes}} minutos.',
    variables: ['barbershop', 'code', 'minutes'],
  },
  {
    key: NotificationTemplateKey.APPOINTMENT_CONFIRMED,
    providerTemplateName: 'appointment_confirmed',
    bodyTemplate:
      '✅ *Agendamento confirmado!*\n\n' +
      'Oi {{customer}}, tá tudo certo pro seu horário na *{{barbershop}}*.\n\n' +
      '✂️ *Serviço:* {{service}}\n' +
      '💈 *Profissional:* {{barber}}\n' +
      '📅 *Quando:* {{datetime}}\n\n' +
      'Chegue uns minutinhos antes pra gente começar no horário. ' +
      'Precisando remarcar, é só usar o app.\n\n' +
      'Te esperamos! 🤝',
    variables: ['customer', 'barbershop', 'service', 'barber', 'datetime'],
  },
  {
    key: NotificationTemplateKey.APPOINTMENT_REMINDER_24H,
    providerTemplateName: 'appointment_reminder_24h',
    bodyTemplate:
      '⏰ *Seu horário é amanhã!*\n\n' +
      'Oi {{customer}}, passando pra lembrar do seu agendamento na *{{barbershop}}*.\n\n' +
      '✂️ {{service}} com {{barber}}\n' +
      '🕐 Amanhã, às *{{time}}*\n\n' +
      'Se não puder vir, avise pelo app — assim a gente libera o horário pra outra pessoa. 🙏',
    variables: ['customer', 'time', 'service', 'barber', 'barbershop'],
  },
  {
    key: NotificationTemplateKey.APPOINTMENT_REMINDER_2H,
    providerTemplateName: 'appointment_reminder_2h',
    bodyTemplate:
      '💈 *Falta pouco, {{customer}}!*\n\n' +
      'Seu horário na *{{barbershop}}* é hoje às *{{time}}*.\n\n' +
      'A cadeira já é sua. Até daqui a pouco! ✂️',
    variables: ['customer', 'barbershop', 'time'],
  },
  {
    key: NotificationTemplateKey.APPOINTMENT_CANCELLED,
    providerTemplateName: 'appointment_cancelled',
    bodyTemplate:
      'Oi {{customer}}, seu horário de {{datetime}} na *{{barbershop}}* foi cancelado.\n\n' +
      'Quando quiser marcar outro, é só abrir o app e escolher o melhor dia pra você. 💈',
    variables: ['customer', 'datetime', 'barbershop'],
  },
  {
    key: NotificationTemplateKey.APPOINTMENT_RESCHEDULED,
    providerTemplateName: 'appointment_rescheduled',
    bodyTemplate:
      '🔄 *Horário remarcado!*\n\n' +
      'Oi {{customer}}, seu agendamento na *{{barbershop}}* mudou de horário.\n\n' +
      '📅 *Agora é:* {{datetime}}\n' +
      '💈 *Com:* {{barber}}\n\n' +
      'Já está anotado por aqui. Te esperamos! 🤝',
    variables: ['customer', 'barbershop', 'datetime', 'barber'],
  },
  {
    key: NotificationTemplateKey.POST_APPOINTMENT,
    providerTemplateName: 'post_appointment',
    bodyTemplate:
      'E aí {{customer}}, tudo certo com o atendimento de hoje? ✂️\n\n' +
      'A *{{barbershop}}* agradece a preferência! Sua opinião ajuda a gente a melhorar sempre — ' +
      'e quando quiser voltar, é só marcar pelo app. 💈',
    variables: ['customer', 'barbershop'],
  },
  {
    key: NotificationTemplateKey.PLAN_ACTIVATED,
    providerTemplateName: 'plan_activated',
    bodyTemplate:
      'Boas-vindas ao {{plan}}, {{customer}}! Seus benefícios na {{barbershop}} já estão liberados.',
    variables: ['plan', 'customer', 'barbershop'],
  },
  {
    key: NotificationTemplateKey.PAYMENT_APPROVED,
    providerTemplateName: 'payment_approved',
    bodyTemplate: 'Pagamento de {{amount}} confirmado, {{customer}}. Obrigado!',
    variables: ['amount', 'customer'],
  },
  {
    key: NotificationTemplateKey.PAYMENT_PENDING,
    providerTemplateName: 'payment_pending',
    bodyTemplate:
      'Olá {{customer}}, o pagamento do seu plano {{plan}} está pendente. Finalize aqui: {{link}}',
    variables: ['customer', 'plan', 'link'],
  },
  {
    key: NotificationTemplateKey.PAYMENT_FAILED,
    providerTemplateName: 'payment_failed',
    bodyTemplate:
      'Olá {{customer}}, não conseguimos processar a cobrança do plano {{plan}}. Atualize seus dados: {{link}}',
    variables: ['customer', 'plan', 'link'],
  },
  {
    key: NotificationTemplateKey.SUBSCRIPTION_DUE,
    providerTemplateName: 'subscription_due',
    bodyTemplate: 'Olá {{customer}}, seu plano {{plan}} renova em {{date}} no valor de {{amount}}.',
    variables: ['customer', 'plan', 'date', 'amount'],
  },
  {
    key: NotificationTemplateKey.PAYOUT_CODE,
    providerTemplateName: 'payout_code',
    bodyTemplate:
      '{{code}} é o código para confirmar a operação financeira na {{barbershop}}. Expira em {{minutes}} minutos. Não passe a ninguém.',
    variables: ['code', 'barbershop', 'minutes'],
  },
  {
    key: NotificationTemplateKey.PLAN_PIX_DUE,
    providerTemplateName: 'plan_pix_due',
    bodyTemplate:
      'Olá {{customer}}, o PIX da mensalidade do plano {{plan}} ({{amount}}) já está no app e vence em {{date}}.',
    variables: ['customer', 'plan', 'amount', 'date'],
  },
  {
    key: NotificationTemplateKey.SUBSCRIPTION_CANCELLED,
    providerTemplateName: 'subscription_cancelled',
    bodyTemplate:
      'Olá {{customer}}, seu plano {{plan}} na {{barbershop}} foi cancelado. Sentiremos sua falta!',
    variables: ['customer', 'plan', 'barbershop'],
  },
  {
    key: NotificationTemplateKey.RECURRENCE_CREATED,
    providerTemplateName: 'recurrence_created',
    bodyTemplate:
      '📌 *Horário fixo garantido!*\n\n' +
      'Oi {{customer}}, a partir de agora a cadeira é sua toda *{{weekday}} às {{time}}* ' +
      'na *{{barbershop}}*.\n\n' +
      '✂️ *Serviço:* {{service}}\n' +
      '💈 *Profissional:* {{barber}}\n\n' +
      'Você recebe o lembrete de cada semana normalmente. Se não puder vir num dia, ' +
      'cancele só aquela semana pelo app — o horário fixo continua. 🤝',
    variables: ['customer', 'weekday', 'time', 'barbershop', 'service', 'barber'],
  },
  {
    key: NotificationTemplateKey.RECURRENCE_WEEK_SKIPPED,
    providerTemplateName: 'recurrence_week_skipped',
    bodyTemplate:
      'Oi {{customer}}, não conseguimos reservar seu horário fixo na *{{barbershop}}* ' +
      'em: *{{dates}}*.\n\n' +
      'Escolha outro horário pra essa semana por aqui: {{link}}\n\n' +
      'Nas outras semanas, seu horário fixo segue normal. 💈',
    variables: ['customer', 'barbershop', 'dates', 'link'],
  },
  {
    key: NotificationTemplateKey.RECURRENCE_CANCELLED,
    providerTemplateName: 'recurrence_cancelled',
    bodyTemplate:
      'Oi {{customer}}, seu horário fixo de *{{weekday}} às {{time}}* na *{{barbershop}}* ' +
      'foi encerrado: {{reason}}.\n\n' +
      'Quando quiser, é só marcar de novo pelo app. 💈',
    variables: ['customer', 'weekday', 'time', 'barbershop', 'reason'],
  },
] as const;

const BY_KEY: ReadonlyMap<string, NotificationTemplateDefinition> = new Map(
  DEFAULT_NOTIFICATION_TEMPLATES.map((template) => [template.key, template]),
);

/** Template padrão de uma chave. `undefined` para chave desconhecida. */
export function defaultTemplateFor(
  key: NotificationTemplateKey | string,
): NotificationTemplateDefinition | undefined {
  return BY_KEY.get(key);
}

/** `{{nome}}`, com espaços tolerados em volta do nome. */
const PLACEHOLDER = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

/** Nomes citados por um corpo de template, sem repetição e na ordem de uso. */
export function extractTemplatePlaceholders(bodyTemplate: string): readonly string[] {
  const found = new Set<string>();

  for (const match of bodyTemplate.matchAll(PLACEHOLDER)) {
    const name = match[1];
    if (name) found.add(name);
  }

  return [...found];
}

/**
 * Substitui os placeholders pelos valores.
 *
 * **Lança quando falta um valor**, de propósito. A alternativa — deixar
 * `{{customer}}` no texto ou trocá-lo por vazio — produziria uma mensagem
 * entregue e sem sentido, que é o pior resultado possível: o cliente lê, a
 * barbearia não fica sabendo, e nenhum log acusa nada. Falhar aqui interrompe
 * o envio antes de qualquer coisa sair.
 */
export function renderNotificationTemplate(bodyTemplate: string, values: TemplateValues): string {
  const missing: string[] = [];

  const rendered = bodyTemplate.replace(PLACEHOLDER, (_full, name: string) => {
    const value = values[name];

    if (value === undefined || value === null) {
      missing.push(name);
      return '';
    }

    return String(value);
  });

  if (missing.length > 0) {
    throw new Error(
      `Template sem valor para ${missing.map((name) => `{{${name}}}`).join(', ')}. ` +
        'Uma mensagem entregue com placeholder é pior do que uma mensagem que não sai.',
    );
  }

  return rendered;
}

/**
 * Converte os valores nomeados no array POSICIONAL que a Meta espera.
 *
 * Uma variável declarada e ausente vira string vazia em vez de erro: aqui já
 * passamos da renderização, e o texto que o cliente vê é o `renderedBody`.
 * Recusar o envio nesse ponto trocaria uma mensagem com um campo vazio por
 * nenhuma mensagem.
 */
export function orderTemplateVariables(
  variables: readonly string[],
  values: TemplateValues,
): readonly string[] {
  return variables.map((name) => values[name] ?? '');
}

/**
 * Como um atendimento com encaixes é chamado nas mensagens e nas telas:
 * "Corte + Lavar cabelo". Vai na variável `{{service}}` dos templates que já
 * existem — assim nenhum template aprovado na Meta precisa ser reaprovado.
 */
export function serviceLabel(
  principal: string,
  addOns: readonly { readonly name: string }[],
): string {
  return [principal, ...addOns.map((a) => a.name)].join(' + ');
}
